import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { SELFEVAL_SECTIONS, sectionAverage, ratingColour } from "@/lib/selfEval";
import { Loader2, Plus, Star, Trash2, ChevronRight } from "lucide-react";

export default function SelfEvalSection({ studentId }) {
  const navigate = useNavigate();
  const [evals, setEvals] = useState(null);

  const load = useCallback(() => {
    api.get(`/self-evaluations?student_id=${studentId}`).then((r) => setEvals(r.data)).catch(() => setEvals([]));
  }, [studentId]);
  useEffect(() => { load(); }, [load]);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Delete this self-evaluation?")) return;
    await api.delete(`/self-evaluations/${id}`);
    load();
  };

  const overall = (ratings) => {
    const avgs = SELFEVAL_SECTIONS.map((s) => sectionAverage(ratings, s)).filter((v) => v != null);
    if (!avgs.length) return null;
    return Math.round((avgs.reduce((a, b) => a + b, 0) / avgs.length) * 10) / 10;
  };

  if (evals === null)
    return <div className="flex justify-center py-10 text-slate-300"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-['Outfit'] text-lg font-bold text-slate-800 flex items-center gap-2">
              <Star size={20} className="text-[#9A7AA0]" /> Get to Know Me
            </h3>
            <p className="text-sm text-slate-400 mt-0.5">A pupil self-reflection on social &amp; emotional skills — great as a baseline in September and again in the summer to measure growth.</p>
          </div>
          <button data-testid="new-selfeval-btn" onClick={() => navigate(`/student/${studentId}/self-eval`)}
            className="bg-[#4A5D6B] hover:bg-[#3A4A56] text-white rounded-full px-5 py-2.5 font-semibold font-['Outfit'] transition flex items-center gap-2 shrink-0">
            <Plus size={18} /> New self-evaluation
          </button>
        </div>
      </div>

      {evals.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8" data-testid="selfeval-empty">No self-evaluations yet. Start one to capture how the pupil sees themselves.</p>
      ) : (
        <div className="space-y-3">
          {evals.map((ev) => {
            const avg = overall(ev.ratings);
            return (
              <button key={ev.id} data-testid={`selfeval-card-${ev.id}`} onClick={() => navigate(`/student/${studentId}/self-eval/${ev.id}`)}
                className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-5 hover:shadow-[0_8px_30px_rgba(42,59,71,0.09)] hover:-translate-y-0.5 transition-all flex items-center gap-4">
                <span className="h-11 w-11 rounded-2xl bg-[#9A7AA0]/15 text-[#7a5e80] flex items-center justify-center shrink-0"><Star size={20} /></span>
                <div className="flex-1 min-w-0">
                  <p className="font-['Outfit'] font-semibold text-slate-800">{ev.title || "Self-evaluation"}</p>
                  <p className="text-xs text-slate-400">{new Date(ev.created_at).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" })}{ev.summary ? " · Summary added" : ""}</p>
                </div>
                {avg != null && (
                  <span className="text-sm font-bold text-white rounded-full px-3 py-1" style={{ backgroundColor: ratingColour(Math.round(avg)) }}>Avg {avg}</span>
                )}
                <span data-testid={`delete-selfeval-${ev.id}`} onClick={(e) => remove(e, ev.id)} className="text-slate-300 hover:text-red-500 p-1"><Trash2 size={16} /></span>
                <ChevronRight size={18} className="text-slate-300 shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
